import React from 'react';
import PropTypes from 'prop-types';
import fr from 'facet-rest';
import {
  SET_SECURITY_TYPE,
  SET_EXCLUDED_ACCOUNT_IDS,
  SET_ACCOUNT_TYPES,
  SET_COLLECTION,
  LOADING_STATE,
  ERROR_STATE,
  loadFromLink,
} from './PortfolioActions';

const PortfolioStateContext = React.createContext(undefined);
const PortfolioDispatchContext = React.createContext(undefined);
const defaultState = {
  selfLink: null,
  excludedAccountIds: [],
  accountTypes: null,
  portfolioCollection: null,
  securityType: null,
  loading: false,
  error: null,
};

function init(state) {
  return {
    ...defaultState,
    ...state,
  };
}

function reducer(state, action) {
  switch (action.type) {
    case SET_COLLECTION: {
      const { collection } = action;

      return {
        ...state,
        portfolioCollection: collection,
        loading: false,
      };
    }
    case LOADING_STATE: {
      const { loading } = action;

      return {
        ...state,
        loading,
      };
    }
    case SET_ACCOUNT_TYPES: {
      const { accountTypes } = action;
      return {
        ...state,
        accountTypes,
      };
    }
    case SET_EXCLUDED_ACCOUNT_IDS: {
      const { ids } = action;
      return {
        ...state,
        excludedAccountIds: ids,
      };
    }
    case SET_SECURITY_TYPE: {
      const { securityType } = action;
      return {
        ...state,
        securityType,
      };
    }
    case ERROR_STATE: {
      const { error } = action;

      return {
        ...state,
        error,
      };
    }
    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
}

function PortfolioProvider({ children, initialState, portfolioRelationship }) {
  const [state, dispatch] = React.useReducer(
    reducer,
    { ...initialState, selfLink: portfolioRelationship },
    init,
  );

  React.useEffect(() => {
    if (!state.loading && !state.portfolioCollection && portfolioRelationship) {
      loadFromLink(portfolioRelationship, dispatch, state);
    }
  }, [portfolioRelationship]);

  return (
    <PortfolioStateContext.Provider value={state}>
      <PortfolioDispatchContext.Provider value={dispatch}>
        {children}
      </PortfolioDispatchContext.Provider>
    </PortfolioStateContext.Provider>
  );
}

function usePortfolioStateContext() {
  const context = React.useContext(PortfolioStateContext);

  if (context === undefined) {
    throw new Error(
      'usePortfolioStateContext must be used within a PortfolioProvider',
    );
  }

  return context;
}

function usePortfolioDispatchContext() {
  const context = React.useContext(PortfolioDispatchContext);

  if (context === undefined) {
    throw new Error(
      'usePortfolioDispatchContext must be used within a PortfolioProvider',
    );
  }

  return context;
}

PortfolioProvider.defaultProps = {
  initialState: defaultState,
};

PortfolioProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
  portfolioRelationship: PropTypes.instanceOf(fr.Link).isRequired,
  initialState: PropTypes.shape({
    error: PropTypes.instanceOf(Error),
    loading: PropTypes.bool,
    portfolioCollection: PropTypes.instanceOf(fr.Collection),
  }),
};

export {
  PortfolioProvider,
  usePortfolioStateContext,
  usePortfolioDispatchContext,
};
